import { ActionEnum } from '@vendia/management-api-types'

import { ActionGroup, ActionGroupMap, ResourceType } from './types'

export const ACTION_GROUPS: ActionGroupMap = {
  organization: {
    label: 'Organization',
    description: 'View organization details and member list',
    icon: 'lucide-house',
    actions: [ActionEnum.OrgAll, ActionEnum.OrgGet, ActionEnum.OrgListUsers],
  },
  roles: {
    label: 'Roles',
    description: 'Create, edit, and assign permission roles to users',
    icon: 'lucide-shield',
    actions: [
      ActionEnum.RoleAll,
      ActionEnum.RoleGet,
      ActionEnum.RoleCreate,
      ActionEnum.RoleUpdate,
      ActionEnum.RoleDeactivate,
      ActionEnum.RoleAssign,
    ],
  },
  users: {
    label: 'Users',
    description: 'Invite new members and manage existing user accounts',
    icon: 'lucide-users',
    actions: [
      ActionEnum.UserAll,
      ActionEnum.UserGet,
      ActionEnum.UserCreate,
      ActionEnum.UserInvite,
      ActionEnum.UserSetRole,
      ActionEnum.UserSetEmail,
      ActionEnum.UserDeactivate,
      ActionEnum.UserDelete,
      ActionEnum.UserDeleteRole,
      ActionEnum.UserDelegateNodeJoin,
    ],
  },
  projects: {
    label: 'Projects',
    description: 'Create, configure, and manage Vendia projects and workspaces',
    icon: 'lucide-folder',
    actions: [
      ActionEnum.UniAll,
      ActionEnum.UniGet,
      ActionEnum.UniCreate,
      ActionEnum.UniJoin,
      ActionEnum.UniInvite,
      ActionEnum.UniQuery,
      ActionEnum.UniMutate,
      ActionEnum.UniEvolveSchema,
      ActionEnum.UniReset,
      ActionEnum.UniDelete,
      ActionEnum.UniDeleteNode,
    ],
  },
  data: {
    label: 'Entity Data',
    description: 'Read and write data stored in your project entities',
    icon: 'lucide-database',
    actions: [ActionEnum.DataAll, ActionEnum.DataRead],
  },
  tables: {
    label: 'Vendia Tables',
    description: 'Query analytical data using Vendia Tables',
    icon: 'lucide-table-2',
    actions: [ActionEnum.TableAll, ActionEnum.TableRead],
  },
  apiCredentials: {
    label: 'API Credentials',
    description: 'Create and manage API keys for programmatic access and service accounts',
    icon: 'key',
    actions: [ActionEnum.ApicredsAll, ActionEnum.ApicredsGet, ActionEnum.ApicredsCreate, ActionEnum.ApicredsDeactivate],
  },
  workflows: {
    label: 'Workflows',
    description: 'View and manage Vendia Workflows (low-code/no-code data integration and sharing)',
    icon: 'workflow',
    actions: [ActionEnum.WorkflowsAll, ActionEnum.WorkflowsGet, ActionEnum.WorkflowsUpdate],
  },
}

// Order of groups for display
export const ACTION_GROUP_ORDER = [
  'organization',
  'roles',
  'users',
  'projects',
  'data',
  'tables',
  'apiCredentials',
  'workflows',
]

/**
 * Friendly display names for each action
 * These are human-readable names that explain what each permission does
 * Set `deprecated: true` to hide an action from the add capability modal while still supporting display
 */
export const ACTION_FRIENDLY_NAMES: Record<string, { name: string; description?: string; deprecated?: boolean }> = {
  // Organization
  [ActionEnum.OrgGet]: {
    name: 'View Organization',
    description: 'View organization details and settings',
  },
  [ActionEnum.OrgListUsers]: {
    name: 'List Members',
    description: 'View the list of organization members',
  },
  [ActionEnum.OrgAll]: {
    name: 'Full Organization Access',
    description: 'All organization-related permissions',
  },

  // Roles
  [ActionEnum.RoleGet]: {
    name: 'View Roles',
    description: 'View role details and permissions',
  },
  [ActionEnum.RoleCreate]: {
    name: 'Create Roles',
    description: 'Create new roles',
  },
  [ActionEnum.RoleUpdate]: {
    name: 'Edit Roles',
    description: 'Modify existing role permissions',
  },
  [ActionEnum.RoleDeactivate]: {
    name: 'Deactivate Roles',
    description: 'Disable existing roles',
  },
  [ActionEnum.RoleAssign]: {
    name: 'Assign Roles',
    description: 'Assign roles to users',
  },
  [ActionEnum.RoleAll]: {
    name: 'Full Role Management',
    description: 'All role-related permissions',
  },

  // Users
  [ActionEnum.UserGet]: {
    name: 'View Users',
    description: 'View user profiles and details',
  },
  [ActionEnum.UserCreate]: {
    name: 'Create Users',
    description: 'Create new user accounts',
  },
  [ActionEnum.UserInvite]: {
    name: 'Invite Users',
    description: 'Send invitations to new members',
  },
  [ActionEnum.UserSetRole]: {
    name: 'Change User Roles',
    description: "Modify users' assigned roles",
  },
  [ActionEnum.UserSetEmail]: {
    name: 'Change User Email',
    description: "Update users' email addresses",
  },
  [ActionEnum.UserDeactivate]: {
    name: 'Deactivate Users',
    description: 'Disable user accounts',
  },
  [ActionEnum.UserDelete]: {
    name: 'Delete Users',
    description: 'Permanently remove user accounts',
  },
  [ActionEnum.UserDeleteRole]: {
    name: 'Remove User Roles',
    description: 'Remove role assignments from users',
  },
  [ActionEnum.UserDelegateNodeJoin]: {
    name: 'Delegate Joining Projects',
    description: 'Typically used to allow Vendia staff to add project workspaces on behalf of users',
  },
  [ActionEnum.UserAll]: {
    name: 'Full User Management',
    description: 'All user-related permissions',
  },

  // Projects (Unis)
  [ActionEnum.UniGet]: {
    name: 'View Projects',
    description: 'View project details and configuration',
  },
  [ActionEnum.UniCreate]: {
    name: 'Create Projects',
    description: 'Create new Vendia projects',
  },
  [ActionEnum.UniJoin]: {
    name: 'Join Projects',
    description: 'Join existing projects as a participant',
  },
  [ActionEnum.UniInvite]: {
    name: 'Invite to Projects',
    description: 'Invite others to add a workspace to a project',
  },
  // Deprecated: was used for GraphQL access, no longer needed
  [ActionEnum.UniQuery]: {
    name: 'Query Project Data',
    description: 'Deprecated permission - no longer used',
    deprecated: true,
  },
  [ActionEnum.UniMutate]: {
    name: 'Modify Project Settings',
    description: 'Change project configuration and settings',
  },
  [ActionEnum.UniEvolveSchema]: {
    name: 'Update Schema',
    description: `Modify the project's operational data schema`,
  },
  [ActionEnum.UniReset]: {
    name: 'Reset Projects',
    description: 'Reset project data and state',
  },
  [ActionEnum.UniDelete]: {
    name: 'Delete Projects',
    description: 'Permanently delete projects',
  },
  [ActionEnum.UniDeleteNode]: {
    name: 'Delete Project Workspaces',
    description: 'Remove workspaces from projects',
  },
  [ActionEnum.UniAll]: {
    name: 'Full Project Access',
    description: 'All project-related permissions',
  },

  // Data
  [ActionEnum.DataRead]: {
    name: 'Read Entity Data',
    description: 'Read data from project entities',
  },
  [ActionEnum.DataAll]: {
    name: 'Full Data Access',
    description: 'Read and write all entity data',
  },

  // Tables
  [ActionEnum.TableRead]: {
    name: 'Query Tables',
    description: 'Execute queries on Vendia tables',
  },
  [ActionEnum.TableAll]: {
    name: 'Full Table Access',
    description: 'All Vendia table permissions',
  },

  // API Credentials
  [ActionEnum.ApicredsGet]: {
    name: 'View API Credentials',
    description: 'View existing API credentials',
  },
  [ActionEnum.ApicredsCreate]: {
    name: 'Create API Credentials',
    description: 'Generate new API credentials',
  },
  [ActionEnum.ApicredsDeactivate]: {
    name: 'Deactivate API Credentials',
    description: 'Permits deactivation of API credentials',
  },
  [ActionEnum.ApicredsAll]: {
    name: 'Full API Credential Management',
    description: 'All API credential permissions',
  },

  // Workflows
  [ActionEnum.WorkflowsGet]: {
    name: 'View Workflows',
    description: 'View workflow configurations',
  },
  [ActionEnum.WorkflowsUpdate]: {
    name: 'Edit Workflows',
    description: 'Modify workflow configurations',
  },
  [ActionEnum.WorkflowsAll]: {
    name: 'Full Workflow Access',
    description: 'All workflow permissions',
  },
}

/**
 * Get the friendly name for an action
 */
export function getFriendlyActionName(action: ActionEnum | string): string {
  const friendly = ACTION_FRIENDLY_NAMES[action]
  if (friendly) {
    return friendly.name
  }
  // Fallback to formatted name
  return formatActionName(action)
}

/**
 * Get the friendly description for an action
 */
export function getFriendlyActionDescription(action: ActionEnum | string): string | null {
  const friendly = ACTION_FRIENDLY_NAMES[action]
  return friendly?.description ?? null
}

/**
 * Check if an action is deprecated (should be hidden from selection but still displayed if present)
 */
export function isDeprecatedAction(action: ActionEnum | string): boolean {
  const friendly = ACTION_FRIENDLY_NAMES[action]
  return friendly?.deprecated === true
}

/**
 * Format an action enum value for display (fallback)
 * e.g. ActionEnum.DataRead -> "data read"
 * UNI_ actions display as "Project" instead of "Uni"
 */
export function formatActionName(action: ActionEnum | string): string {
  const formatted = action.toLowerCase().replace(/_/g, ' ')
  // Replace "uni" with "project" for better UX
  return formatted.replace('uni', 'project')
}

/**
 * Get the group key for a given action
 */
export function getActionGroupKey(action: ActionEnum): string | null {
  for (const [groupKey, group] of Object.entries(ACTION_GROUPS)) {
    if (group.actions.includes(action)) {
      return groupKey
    }
  }
  return null
}

/**
 * Get the group for a given action
 */
export function getActionGroup(action: ActionEnum): ActionGroup | null {
  const groupKey = getActionGroupKey(action)
  return groupKey ? ACTION_GROUPS[groupKey] : null
}

/**
 * Suggest the recommended resource type based on the action
 */
export function getRecommendedResourceType(action: ActionEnum): ResourceType {
  if (action.startsWith('ORG_') || action.startsWith('ROLE_')) {
    return 'OrganizationResource'
  }
  if (action.startsWith('UNI_')) {
    return 'UniResource'
  }
  if (action.startsWith('USER_')) {
    return 'NameResource'
  }
  if (action.startsWith('DATA_') || action.startsWith('TABLE_')) {
    return 'VendiaResource'
  }
  // Default for APICREDS_, GROUP_, WORKFLOWS_, etc.
  return 'OrganizationResource'
}

/**
 * Get all actions as a flat array (for filtering/validation)
 */
export function getAllActions(): ActionEnum[] {
  return Object.values(ACTION_GROUPS).flatMap((group) => group.actions)
}

/**
 * Check if an action is known (in our groups)
 */
export function isKnownAction(action: ActionEnum): boolean {
  return getAllActions().includes(action)
}

/**
 * Actions that only take OrganizationResource (auto-scoped to current org)
 * These don't need a resource builder - they're auto-added
 */
export function isOrgOnlyAction(action: ActionEnum): boolean {
  return (
    action.startsWith('ORG_') ||
    action.startsWith('ROLE_') ||
    action.startsWith('APICREDS_') ||
    action.startsWith('WORKFLOWS_')
  )
}

/**
 * Actions that only take NameResource
 */
export function isNameResourceOnlyAction(action: ActionEnum): boolean {
  return action === ActionEnum.UserInvite
}

/**
 * Get the allowed resource types for a given action
 * Returns null if the action auto-uses OrganizationResource
 */
export function getAllowedResourceTypes(action: ActionEnum): ResourceType[] | null {
  // Org-only actions don't need resource type selection
  if (isOrgOnlyAction(action)) {
    return null
  }

  // USER_INVITE only takes NameResource
  if (action === ActionEnum.UserInvite) {
    return ['NameResource']
  }

  // UNI_ actions can use UniResource, NameResource, or VendiaResource
  if (action.startsWith('UNI_')) {
    return ['UniResource', 'VendiaResource', 'NameResource']
  }

  // USER_ actions (except USER_INVITE) can use NameResource or VendiaResource
  if (action.startsWith('USER_')) {
    return ['NameResource', 'VendiaResource']
  }

  // DATA_ and TABLE_ actions use VendiaResource
  if (action.startsWith('DATA_') || action.startsWith('TABLE_')) {
    return ['VendiaResource']
  }

  // Default: allow all types
  return ['VendiaResource', 'UniResource', 'NameResource', 'OrganizationResource']
}
